import { useMemo, useState } from 'react';
import { AlertCircle, BrainCircuit, FolderTree, Layers3, Target } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from '../ui/chart';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ArchiveRecord, DocumentFolder } from '../../types';
import { KMeansPoint, normalizeVectors, runKMeans } from '../../utils/kmeans';

interface DocumentArchiveKMeansProps {
  documents: ArchiveRecord[];
  folders: DocumentFolder[];
  scopeLabel: string;
}

interface DocumentClusterPayload {
  document: ArchiveRecord;
  extension: string;
  folderDepth: number;
  accessScore: number;
  collaborationScore: number;
  ageInDays: number;
}

const CLUSTER_COLORS = ['#0f766e', '#2563eb', '#ea580c', '#9333ea'];
const FILE_TYPE_CHART_COLOR = '#0f766e';

function toSeriesKey(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

function getFileExtension(fileName?: string) {
  return fileName?.split('.').pop()?.toLowerCase() || 'unknown';
}

function getFolderDepth(folderId: string | undefined, foldersById: Map<string, DocumentFolder>) {
  let depth = 0;
  let currentFolderId = folderId;

  while (currentFolderId) {
    const folder = foldersById.get(currentFolderId);
    if (!folder?.parentId) {
      depth += folder ? 1 : 0;
      break;
    }

    depth += 1;
    currentFolderId = folder.parentId;
  }

  return depth;
}

function getAccessScore(document: ArchiveRecord) {
  if (document.isPublic) {
    return 1;
  }

  if (document.allowedRoles?.includes('same_course')) {
    return 0.8;
  }

  if (document.allowedRoles?.length) {
    return 0.7;
  }

  if ((document.targetUsers?.length || 0) > 0 || (document.allowedUsers?.length || 0) > 0) {
    return 0.45;
  }

  return 0.15;
}

function getClusterLabel(payloads: DocumentClusterPayload[]) {
  const averageAccess = payloads.reduce((sum, payload) => sum + payload.accessScore, 0) / payloads.length;
  const averageCollaboration = payloads.reduce((sum, payload) => sum + payload.collaborationScore, 0) / payloads.length;
  const averageAge = payloads.reduce((sum, payload) => sum + payload.ageInDays, 0) / payloads.length;

  if (averageAccess >= 0.75) {
    return 'Broadly shared records';
  }

  if (averageCollaboration >= 2) {
    return 'Collaborative review set';
  }

  if (averageAge <= 45) {
    return 'Recently updated files';
  }

  return 'Focused private archive';
}

function formatDays(value: number) {
  if (value < 1) {
    return 'Today';
  }

  if (value < 30) {
    return `${Math.round(value)} days`;
  }

  return `${(value / 30).toFixed(1)} months`;
}

export function DocumentArchiveKMeans({ documents, folders, scopeLabel }: DocumentArchiveKMeansProps) {
  const [clusterCount, setClusterCount] = useState('3');

  const analytics = useMemo(() => {
    if (documents.length < 2) {
      return null;
    }

    const foldersById = new Map(folders.map((folder) => [folder.id, folder]));
    const now = Date.now();

    const rawVectors = documents.map((document) => {
      const extension = getFileExtension(document.fileName);
      const collaborationScore = Math.max(document.targetUsers?.length || 0, document.allowedUsers?.length || 0);
      const ageInDays = Math.max(0, (now - new Date(document.dateModified).getTime()) / (1000 * 60 * 60 * 24));
      const folderDepth = getFolderDepth(document.folderId, foldersById);
      const accessScore = getAccessScore(document);

      return {
        document,
        extension,
        folderDepth,
        accessScore,
        collaborationScore,
        ageInDays,
        vector: [
          Math.log10((document.fileSize || 0) + 1),
          ageInDays,
          document.title.length,
          document.description.length,
          collaborationScore,
          accessScore,
          folderDepth,
          document.courseFilter ? 1 : 0,
        ],
      };
    });

    const normalizedVectors = normalizeVectors(rawVectors.map((item) => item.vector));
    const points: Array<KMeansPoint<DocumentClusterPayload>> = rawVectors.map((item, index) => ({
      id: item.document.id,
      label: item.document.title,
      vector: normalizedVectors[index],
      payload: {
        document: item.document,
        extension: item.extension,
        folderDepth: item.folderDepth,
        accessScore: item.accessScore,
        collaborationScore: item.collaborationScore,
        ageInDays: item.ageInDays,
      },
    }));

    const result = runKMeans(points, Number(clusterCount));
    const rankedClusters = [...result.clusters]
      .sort((left, right) => right.points.length - left.points.length)
      .map((cluster, index) => {
        const payloads = cluster.points.map((point) => point.payload);
        return {
          ...cluster,
          color: CLUSTER_COLORS[index % CLUSTER_COLORS.length],
          displayLabel: getClusterLabel(payloads),
          averageAge: payloads.reduce((sum, payload) => sum + payload.ageInDays, 0) / payloads.length,
          averageCollaboration:
            payloads.reduce((sum, payload) => sum + payload.collaborationScore, 0) / payloads.length,
          publicCount: payloads.filter((payload) => payload.document.isPublic).length,
          fileTypes: Array.from(new Set(payloads.map((payload) => payload.extension))).slice(0, 3),
        };
      });

    return {
      result,
      clusters: rankedClusters,
    };
  }, [clusterCount, documents, folders]);

  const fileTypeChartData = useMemo(() => {
    return documents
      .reduce<Record<string, number>>((accumulator, document) => {
        const extension = getFileExtension(document.fileName);
        accumulator[extension] = (accumulator[extension] || 0) + 1;
        return accumulator;
      }, {})
      ? Object.entries(
          documents.reduce<Record<string, number>>((accumulator, document) => {
            const extension = getFileExtension(document.fileName);
            accumulator[extension] = (accumulator[extension] || 0) + 1;
            return accumulator;
          }, {}),
        )
          .sort((left, right) => right[1] - left[1])
          .slice(0, 6)
          .map(([extension, count]) => ({
            extension: extension.toUpperCase(),
            count,
            fill: FILE_TYPE_CHART_COLOR,
          }))
      : [];
  }, [documents]);

  const fileTypeChartConfig = {
    count: {
      label: 'Documents',
      color: FILE_TYPE_CHART_COLOR,
    },
  } as const;

  const clusterTrendConfig = useMemo(() => {
    if (!analytics) {
      return {};
    }

    return analytics.clusters.reduce<Record<string, { label: string; color: string }>>((accumulator, cluster) => {
      accumulator[toSeriesKey(cluster.id)] = {
        label: cluster.displayLabel,
        color: cluster.color,
      };
      return accumulator;
    }, {});
  }, [analytics]);

  const clusterTrendData = useMemo(() => {
    if (!analytics) {
      return [];
    }

    const months = Array.from({ length: 6 }, (_, index) => {
      const date = new Date();
      date.setDate(1);
      date.setHours(0, 0, 0, 0);
      date.setMonth(date.getMonth() - (5 - index));

      return {
        key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: date.toLocaleDateString('en-US', { month: 'short' }),
        date,
      };
    });

    return months.map((month) => {
      const counts = analytics.clusters.reduce<Record<string, number>>((accumulator, cluster) => {
        const seriesKey = toSeriesKey(cluster.id);
        accumulator[seriesKey] = cluster.points.filter((point) => {
          const modifiedAt = new Date(point.payload.document.dateModified);
          return (
            modifiedAt.getFullYear() === month.date.getFullYear() &&
            modifiedAt.getMonth() === month.date.getMonth()
          );
        }).length;
        return accumulator;
      }, {});

      return {
        month: month.label,
        ...counts,
      };
    });
  }, [analytics]);

  const maxClusterCount = Math.min(4, documents.length || 4);

  return (
    <Card className="border-slate-200/80 shadow-sm">
      <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <Badge variant="secondary" className="w-fit gap-1 bg-teal-50 text-teal-700">
            <BrainCircuit className="h-3.5 w-3.5" />
            K-Means clustering
          </Badge>
          <CardTitle className="text-xl tracking-tight text-slate-900">Document archive grouping</CardTitle>
          <CardDescription className="max-w-3xl text-sm leading-6 text-slate-600">
            Documents in the {scopeLabel} are grouped by file size, recency, sharing breadth, folder depth, and metadata richness.
            This runs locally from archive metadata that is already loaded in the document manager.
          </CardDescription>
        </div>

        <div className="w-full sm:w-44">
          <Select value={clusterCount} onValueChange={setClusterCount} disabled={documents.length < 2}>
            <SelectTrigger>
              <SelectValue placeholder="Cluster count" />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: Math.max(1, maxClusterCount - 1) }, (_, index) => {
                const value = String(index + 2);
                return (
                  <SelectItem key={value} value={value}>
                    {value} clusters
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {!analytics && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              K-Means needs at least two documents in this scope before a cluster model can be computed.
            </AlertDescription>
          </Alert>
        )}

        {analytics && (
          <>
            <div className="grid gap-4 md:grid-cols-4">
              <Card className="border-slate-200/70 bg-slate-50/60">
                <CardHeader className="pb-2">
                  <CardDescription>Documents analyzed</CardDescription>
                  <CardTitle className="text-3xl text-slate-900">{documents.length}</CardTitle>
                </CardHeader>
              </Card>
              <Card className="border-slate-200/70 bg-slate-50/60">
                <CardHeader className="pb-2">
                  <CardDescription>Clusters</CardDescription>
                  <CardTitle className="text-3xl text-slate-900">{analytics.clusters.length}</CardTitle>
                </CardHeader>
              </Card>
              <Card className="border-slate-200/70 bg-slate-50/60">
                <CardHeader className="pb-2">
                  <CardDescription>Iterations</CardDescription>
                  <CardTitle className="text-3xl text-slate-900">{analytics.result.iterations}</CardTitle>
                </CardHeader>
              </Card>
              <Card className="border-slate-200/70 bg-slate-50/60">
                <CardHeader className="pb-2">
                  <CardDescription>Total SSE</CardDescription>
                  <CardTitle className="text-3xl text-slate-900">{analytics.result.totalSse.toFixed(2)}</CardTitle>
                </CardHeader>
              </Card>
            </div>

            <div className="grid gap-4 xl:grid-cols-3">
              {analytics.clusters.map((cluster) => (
                <Card key={cluster.id} className="border-slate-200/80">
                  <CardHeader className="space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <CardTitle className="text-base text-slate-900">{cluster.displayLabel}</CardTitle>
                        <CardDescription>
                          {cluster.points.length} document{cluster.points.length === 1 ? '' : 's'} in this cluster
                        </CardDescription>
                      </div>
                      <span className="h-3 w-3 rounded-full" style={{ backgroundColor: cluster.color }} />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {cluster.fileTypes.map((fileType) => (
                        <Badge key={fileType} variant="outline" className="text-xs uppercase">
                          {fileType}
                        </Badge>
                      ))}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm text-slate-600">
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        Public files
                      </span>
                      <strong className="text-slate-900">{cluster.publicCount}</strong>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-2">
                        <Layers3 className="h-4 w-4" />
                        Avg. collaborators
                      </span>
                      <strong className="text-slate-900">{cluster.averageCollaboration.toFixed(1)}</strong>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-2">
                        <FolderTree className="h-4 w-4" />
                        Avg. age
                      </span>
                      <strong className="text-slate-900">{formatDays(cluster.averageAge)}</strong>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1.2fr)]">
              <Card className="border-slate-200/80">
                <CardHeader>
                  <CardTitle className="text-lg text-slate-900">File-type distribution</CardTitle>
                  <CardDescription>
                    Top file extensions in the current archive scope, ranked by document count.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ChartContainer config={fileTypeChartConfig} className="h-[280px] w-full">
                    <BarChart data={fileTypeChartData} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" />
                      <XAxis dataKey="extension" tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                      <ChartTooltip
                        cursor={false}
                        content={
                          <ChartTooltipContent
                            formatter={(value, _name, item) => {
                              return (
                                <div className="flex min-w-32 flex-col gap-1">
                                  <span className="font-medium text-slate-900">{item.payload.extension}</span>
                                  <span className="text-slate-600">Documents: {String(value)}</span>
                                </div>
                              );
                            }}
                          />
                        }
                      />
                      <Bar dataKey="count" fill="var(--color-count)" radius={[10, 10, 0, 0]} />
                    </BarChart>
                  </ChartContainer>
                </CardContent>
              </Card>

              <Card className="border-slate-200/80">
                <CardHeader>
                  <CardTitle className="text-lg text-slate-900">Recent cluster volume</CardTitle>
                  <CardDescription>
                    Monthly document counts per cluster based on each file&apos;s last modified date across the last six months.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ChartContainer config={clusterTrendConfig} className="h-[280px] w-full">
                    <LineChart data={clusterTrendData} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" />
                      <XAxis dataKey="month" tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                      <ChartTooltip
                        cursor={false}
                        content={
                          <ChartTooltipContent
                            formatter={(value, name) => {
                              const configEntry = clusterTrendConfig[String(name)];
                              return (
                                <div className="flex min-w-40 flex-col gap-1">
                                  <span className="font-medium text-slate-900">{configEntry?.label || String(name)}</span>
                                  <span className="text-slate-600">Documents: {String(value)}</span>
                                </div>
                              );
                            }}
                          />
                        }
                      />
                      <ChartLegend content={<ChartLegendContent />} />
                      {analytics.clusters.map((cluster) => {
                        const seriesKey = toSeriesKey(cluster.id);
                        return (
                          <Line
                            key={cluster.id}
                            type="monotone"
                            dataKey={seriesKey}
                            stroke={`var(--color-${seriesKey})`}
                            strokeWidth={3}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        );
                      })}
                    </LineChart>
                  </ChartContainer>
                </CardContent>
              </Card>
            </div>

            <Card className="border-slate-200/80">
              <CardHeader>
                <CardTitle className="text-lg text-slate-900">Document assignments</CardTitle>
                <CardDescription>
                  Each row shows the cluster selected for a document from the active archive scope.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Document</TableHead>
                        <TableHead>Cluster</TableHead>
                        <TableHead>Visibility</TableHead>
                        <TableHead>Age</TableHead>
                        <TableHead>File size</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.clusters.flatMap((cluster) =>
                        cluster.points
                          .map((point) => ({ cluster, payload: point.payload }))
                          .sort((left, right) => right.payload.document.dateModified.localeCompare(left.payload.document.dateModified))
                          .map(({ cluster: currentCluster, payload }) => (
                            <TableRow key={payload.document.id}>
                              <TableCell>
                                <div className="space-y-1">
                                  <div className="font-medium text-slate-900">{payload.document.title}</div>
                                  <div className="text-xs text-muted-foreground">{payload.document.type}</div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="gap-2 border-slate-300 text-slate-700">
                                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: currentCluster.color }} />
                                  {currentCluster.displayLabel}
                                </Badge>
                              </TableCell>
                              <TableCell>{payload.document.isPublic ? 'Public' : 'Restricted'}</TableCell>
                              <TableCell>{formatDays(payload.ageInDays)}</TableCell>
                              <TableCell>
                                {payload.document.fileSize
                                  ? `${(payload.document.fileSize / 1024 / 1024).toFixed(2)} MB`
                                  : 'Unknown'}
                              </TableCell>
                            </TableRow>
                          )),
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </CardContent>
    </Card>
  );
}